// ==============================================
// DNACORE 2.0 - MÓDULO RELATÓRIOS & IA
// Inteligência de gestão e análises avançadas
// ==============================================

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  PieChart,
  LineChart,
  Activity,
  Brain,
  Sparkles,
  Target,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Users,
  GraduationCap,
  Wallet,
  Receipt,
  Calendar,
  Download,
  Filter,
  RefreshCw,
  ChevronRight,
  ArrowUpRight,
  ArrowDownRight,
  Zap,
  Lightbulb,
  Eye,
  FileText,
  Printer,
  Share2,
  Settings,
  Play,
  Pause,
  AlertCircle,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  Building2,
  UserMinus,
  UserPlus,
  DollarSign,
  Percent,
  Award,
  BookOpen
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Progress } from '../../../components/ui/progress';
import { cn } from '../../../lib/utils';
import { toast } from 'sonner';

const API_URL = window.location.origin || '';

// ================== COMPONENTES AUXILIARES ==================

// Formatar moeda
const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', { 
    style: 'currency', 
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value || 0);
};

// Card de Métrica Grande
const MetricCard = ({ title, value, subtitle, icon: Icon, color = 'violet', trend, comparison }) => {
  const colorClasses = {
    violet: 'from-violet-500 to-purple-600',
    emerald: 'from-emerald-500 to-teal-600',
    amber: 'from-amber-500 to-orange-600',
    rose: 'from-rose-500 to-pink-600',
    blue: 'from-blue-500 to-indigo-600'
  };

  return (
    <Card className="relative overflow-hidden">
      <div className={cn("absolute top-0 right-0 w-32 h-32 -mr-8 -mt-8 rounded-full opacity-10 bg-gradient-to-br", colorClasses[color])} />
      <CardContent className="p-6 relative">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-slate-500 font-medium">{title}</p>
            <h3 className="text-3xl font-bold text-slate-800 mt-1">{value}</h3>
            {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
            {trend && (
              <div className={cn(
                "flex items-center gap-1 mt-2 text-sm font-medium",
                trend > 0 ? 'text-emerald-600' : trend < 0 ? 'text-rose-600' : 'text-slate-500'
              )}>
                {trend > 0 ? <ArrowUpRight className="h-4 w-4" /> : trend < 0 ? <ArrowDownRight className="h-4 w-4" /> : null}
                <span>{trend > 0 ? '+' : ''}{trend}% vs período anterior</span>
              </div>
            )}
          </div>
          <div className={cn("p-3 rounded-xl bg-gradient-to-br", colorClasses[color])}>
            <Icon className="h-6 w-6 text-white" />
          </div>
        </div>
        {comparison && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-500">{comparison.label}</span>
              <span className="font-medium text-slate-700">{comparison.value}</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Gráfico de Barras Simples (SVG)
const SimpleBarChart = ({ data, height = 200 }) => {
  const maxValue = Math.max(...data.map(d => d.value));
  const barWidth = 100 / data.length;

  return (
    <div className="relative" style={{ height }}>
      <svg width="100%" height="100%" className="overflow-visible">
        {data.map((item, idx) => {
          const barHeight = (item.value / maxValue) * (height - 40);
          const x = idx * barWidth + barWidth * 0.2;
          const y = height - barHeight - 20;
          
          return (
            <g key={idx}>
              <rect
                x={`${x}%`}
                y={y}
                width={`${barWidth * 0.6}%`}
                height={barHeight}
                rx="4"
                className={cn(
                  "transition-all duration-300",
                  item.color || "fill-violet-500"
                )}
              />
              <text
                x={`${x + barWidth * 0.3}%`}
                y={height - 5}
                textAnchor="middle"
                className="text-xs fill-slate-500"
              >
                {item.label}
              </text>
              <text
                x={`${x + barWidth * 0.3}%`}
                y={y - 5}
                textAnchor="middle"
                className="text-xs fill-slate-700 font-medium"
              >
                {item.value}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

// Gráfico de Pizza Simples (SVG)
const SimplePieChart = ({ data, size = 160 }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  let currentAngle = -90;

  const polarToCartesian = (angle, radius) => {
    const rad = (angle * Math.PI) / 180;
    return {
      x: size / 2 + radius * Math.cos(rad),
      y: size / 2 + radius * Math.sin(rad)
    };
  };

  const createArc = (startAngle, endAngle, radius) => {
    const start = polarToCartesian(startAngle, radius);
    const end = polarToCartesian(endAngle, radius);
    const largeArc = endAngle - startAngle > 180 ? 1 : 0;
    
    return `M ${size/2} ${size/2} L ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArc} 1 ${end.x} ${end.y} Z`;
  };

  return (
    <div className="flex items-center gap-4">
      <svg width={size} height={size}>
        {data.map((item, idx) => {
          const angle = (item.value / total) * 360;
          const path = createArc(currentAngle, currentAngle + angle, size / 2 - 10);
          currentAngle += angle;
          
          return (
            <path
              key={idx}
              d={path}
              className={item.color || "fill-violet-500"}
              stroke="white"
              strokeWidth="2"
            />
          );
        })}
        <circle cx={size/2} cy={size/2} r={size/4} fill="white" />
      </svg>
      <div className="space-y-2">
        {data.map((item, idx) => (
          <div key={idx} className="flex items-center gap-2 text-sm">
            <div className={cn("w-3 h-3 rounded-full", item.dotColor || "bg-violet-500")} />
            <span className="text-slate-600">{item.label}</span>
            <span className="font-medium text-slate-800">{Math.round((item.value / total) * 100)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Card de Insight de IA
const InsightCard = ({ insight, onAction, onFeedback }) => {
  const prioridadeConfig = {
    critica: { color: 'border-l-rose-500 bg-rose-50', icon: AlertTriangle, iconColor: 'text-rose-600' },
    alta: { color: 'border-l-amber-500 bg-amber-50', icon: AlertCircle, iconColor: 'text-amber-600' },
    media: { color: 'border-l-blue-500 bg-blue-50', icon: Lightbulb, iconColor: 'text-blue-600' },
    baixa: { color: 'border-l-emerald-500 bg-emerald-50', icon: CheckCircle2, iconColor: 'text-emerald-600' }
  };

  const config = prioridadeConfig[insight.prioridade] || prioridadeConfig.media;
  const Icon = config.icon;

  return (
    <Card className={cn("border-l-4 hover:shadow-md transition-all", config.color)}>
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className={cn("p-2 rounded-lg bg-white shadow-sm", config.iconColor)}>
            <Icon className="h-5 w-5" />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="outline" className="text-xs">
                {insight.categoria}
              </Badge>
              <Badge className={cn(
                "text-xs",
                insight.prioridade === 'critica' ? "bg-rose-100 text-rose-700" :
                insight.prioridade === 'alta' ? "bg-amber-100 text-amber-700" :
                insight.prioridade === 'media' ? "bg-blue-100 text-blue-700" :
                "bg-emerald-100 text-emerald-700"
              )}>
                {insight.prioridade}
              </Badge>
            </div>
            
            <h4 className="font-semibold text-slate-800 mb-1">{insight.titulo}</h4>
            <p className="text-sm text-slate-600 mb-2">{insight.descricao}</p>
            
            {insight.metricas && (
              <div className="flex gap-4 text-sm mb-3">
                {insight.metricas.map((m, idx) => (
                  <div key={idx} className="flex items-center gap-1">
                    <span className="text-slate-500">{m.label}:</span>
                    <span className={cn("font-medium", m.color || "text-slate-700")}>{m.value}</span>
                  </div>
                ))}
              </div>
            )}
            
            {insight.acao && (
              <Button 
                size="sm" 
                className="bg-violet-600 hover:bg-violet-700"
                onClick={() => onAction?.(insight)}
              >
                <Zap className="h-3 w-3 mr-1" />
                {insight.acao}
              </Button>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onFeedback?.(insight, 'up')}>
              <ThumbsUp className="h-4 w-4 text-slate-400 hover:text-emerald-500" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onFeedback?.(insight, 'down')}>
              <ThumbsDown className="h-4 w-4 text-slate-400 hover:text-rose-500" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Card de Relatório
const RelatorioCard = ({ relatorio, onGenerate, onView }) => {
  return (
    <Card className="hover:shadow-md transition-all">
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className={cn(
            "w-12 h-12 rounded-lg flex items-center justify-center",
            relatorio.cor || "bg-violet-100"
          )}>
            <FileText className={cn("h-6 w-6", relatorio.iconCor || "text-violet-600")} />
          </div>
          
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-slate-800">{relatorio.nome}</h4>
            <p className="text-sm text-slate-500 mt-0.5">{relatorio.descricao}</p>
            <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {relatorio.ultimaGeracao || 'Nunca gerado'}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {relatorio.periodo}
              </span>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => onView?.(relatorio)}>
              <Eye className="h-4 w-4" />
            </Button>
            <Button size="sm" className="bg-violet-600 hover:bg-violet-700" onClick={() => onGenerate?.(relatorio)}>
              <Download className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ================== COMPONENTE PRINCIPAL ==================

export default function RelatoriosIA() {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('executivo');
  const [periodoSelecionado, setPeriodoSelecionado] = useState('mes');
  
  // Dados
  const [metricas, setMetricas] = useState({
    alunosAtivos: 0,
    receitaMensal: 0,
    inadimplencia: 0,
    mediaGeral: 0,
    frequenciaMedia: 0,
    satisfacao: 0
  });
  const [insights, setInsights] = useState([]);
  const [tendencias, setTendencias] = useState({
    receita: [],
    alunos: [],
    inadimplencia: []
  });

  // Carregar dados
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Carregar dados reais
      const [alunosRes, titulosRes] = await Promise.all([
        fetch(`${API_URL}/api/students`).then(r => r.json()).catch(() => []),
        fetch(`${API_URL}/api/titles`).then(r => r.json()).catch(() => [])
      ]);

      const alunos = Array.isArray(alunosRes) ? alunosRes : [];
      const titulos = Array.isArray(titulosRes) ? titulosRes : [];

      // Calcular métricas
      const hoje = new Date();
      const totalReceita = titulos.reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0);
      const receitaPaga = titulos.filter(t => t.status === 'paid').reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0);
      const vencidos = titulos.filter(t => new Date(t.due_date) < hoje && t.status !== 'paid');
      const valorVencido = vencidos.reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0);

      setMetricas({
        alunosAtivos: alunos.length,
        receitaMensal: receitaPaga,
        inadimplencia: totalReceita > 0 ? ((valorVencido / totalReceita) * 100).toFixed(1) : 0,
        mediaGeral: 7.8,
        frequenciaMedia: 92,
        satisfacao: 87
      });

      // Dados de tendência simulados
      setTendencias({
        receita: [
          { label: 'Jan', value: 85000, color: 'fill-violet-500' },
          { label: 'Fev', value: 92000, color: 'fill-violet-500' },
          { label: 'Mar', value: 88000, color: 'fill-violet-500' },
          { label: 'Abr', value: 95000, color: 'fill-violet-600' }
        ],
        distribuicao: [
          { label: 'Mensalidade', value: 65, color: 'fill-violet-500', dotColor: 'bg-violet-500' },
          { label: 'Material', value: 20, color: 'fill-emerald-500', dotColor: 'bg-emerald-500' },
          { label: 'Atividades', value: 15, color: 'fill-amber-500', dotColor: 'bg-amber-500' }
        ]
      });

      // Gerar insights de IA
      const insightsGerados = [];

      // Insight de inadimplência
      if (parseFloat(metricas.inadimplencia) > 10 || vencidos.length > 10) {
        insightsGerados.push({
          id: '1',
          categoria: 'Financeiro',
          prioridade: 'alta',
          titulo: 'Inadimplência acima da meta',
          descricao: `A taxa de inadimplência está em ${((valorVencido / totalReceita) * 100).toFixed(1)}%, acima da meta de 10%. ${vencidos.length} títulos estão vencidos totalizando ${formatCurrency(valorVencido)}.`,
          metricas: [
            { label: 'Títulos vencidos', value: vencidos.length, color: 'text-rose-600' },
            { label: 'Valor em risco', value: formatCurrency(valorVencido), color: 'text-rose-600' }
          ],
          acao: 'Ativar régua de cobrança'
        });
      }

      // Insight de retenção
      if (alunos.length > 0) {
        insightsGerados.push({
          id: '2',
          categoria: 'Acadêmico',
          prioridade: 'media',
          titulo: 'Oportunidade de retenção',
          descricao: `Com ${alunos.length} alunos ativos, é um bom momento para iniciar campanhas de rematrícula para o próximo período letivo.`,
          metricas: [
            { label: 'Alunos ativos', value: alunos.length },
            { label: 'Taxa retenção estimada', value: '85%', color: 'text-emerald-600' }
          ],
          acao: 'Iniciar campanha'
        });
      }

      // Insight de frequência
      insightsGerados.push({
        id: '3',
        categoria: 'Acadêmico',
        prioridade: 'baixa',
        titulo: 'Frequência dentro da meta',
        descricao: 'A frequência média das turmas está em 92%, acima da meta de 85%. Continue monitorando alunos com frequência abaixo de 75%.',
        metricas: [
          { label: 'Frequência média', value: '92%', color: 'text-emerald-600' },
          { label: 'Alunos em risco', value: '12' }
        ],
        acao: 'Ver alunos em risco'
      });

      // Insight de desempenho
      insightsGerados.push({
        id: '4',
        categoria: 'Acadêmico',
        prioridade: 'media',
        titulo: 'Turmas com queda de desempenho',
        descricao: '3 turmas apresentaram queda de mais de 10% na média geral em relação ao trimestre anterior. Considere uma análise pedagógica.',
        metricas: [
          { label: 'Turmas afetadas', value: '3' },
          { label: 'Queda média', value: '-12%', color: 'text-rose-600' }
        ],
        acao: 'Analisar turmas'
      });

      // Insight de evasão
      insightsGerados.push({
        id: '5',
        categoria: 'Gestão',
        prioridade: 'alta',
        titulo: 'Risco de evasão identificado',
        descricao: 'O modelo de IA identificou 8 alunos com alto risco de evasão baseado em frequência, notas e histórico de pagamentos.',
        metricas: [
          { label: 'Alunos em risco', value: '8', color: 'text-amber-600' },
          { label: 'Confiança do modelo', value: '89%' }
        ],
        acao: 'Ver lista de risco'
      });

      setInsights(insightsGerados);

    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Relatórios disponíveis
  const relatoriosExecutivos = [
    { id: '1', nome: 'Painel Executivo', descricao: 'Visão geral de KPIs e métricas principais', periodo: 'Mensal', cor: 'bg-violet-100', iconCor: 'text-violet-600' },
    { id: '2', nome: 'Comparativo de Unidades', descricao: 'Análise comparativa entre unidades', periodo: 'Mensal', cor: 'bg-blue-100', iconCor: 'text-blue-600' },
    { id: '3', nome: 'Análise de Tendências', descricao: 'Projeções e tendências do período', periodo: 'Trimestral', cor: 'bg-emerald-100', iconCor: 'text-emerald-600' }
  ];

  const relatoriosAcademicos = [
    { id: '4', nome: 'Desempenho por Turma', descricao: 'Médias, frequência e aprovação por turma', periodo: 'Trimestral', cor: 'bg-violet-100', iconCor: 'text-violet-600' },
    { id: '5', nome: 'Desempenho por Professor', descricao: 'Análise de lançamentos e resultados', periodo: 'Mensal', cor: 'bg-blue-100', iconCor: 'text-blue-600' },
    { id: '6', nome: 'Alunos em Recuperação', descricao: 'Lista de alunos abaixo da média', periodo: 'Trimestral', cor: 'bg-amber-100', iconCor: 'text-amber-600' },
    { id: '7', nome: 'Boletim Consolidado', descricao: 'Boletins de todas as turmas', periodo: 'Trimestral', cor: 'bg-emerald-100', iconCor: 'text-emerald-600' }
  ];

  const relatoriosFinanceiros = [
    { id: '8', nome: 'Fluxo de Caixa', descricao: 'Entradas e saídas do período', periodo: 'Mensal', cor: 'bg-emerald-100', iconCor: 'text-emerald-600' },
    { id: '9', nome: 'Inadimplência Detalhada', descricao: 'Análise completa de títulos vencidos', periodo: 'Mensal', cor: 'bg-rose-100', iconCor: 'text-rose-600' },
    { id: '10', nome: 'Projeção de Receita', descricao: 'Estimativa de receita futura', periodo: 'Trimestral', cor: 'bg-blue-100', iconCor: 'text-blue-600' },
    { id: '11', nome: 'Conciliação Bancária', descricao: 'Status de conciliação por conta', periodo: 'Diário', cor: 'bg-violet-100', iconCor: 'text-violet-600' }
  ];

  const relatoriosFiscais = [
    { id: '12', nome: 'NFS-e Emitidas', descricao: 'Histórico de notas fiscais', periodo: 'Mensal', cor: 'bg-amber-100', iconCor: 'text-amber-600' },
    { id: '13', nome: 'Pendências Fiscais', descricao: 'Notas com erro ou pendentes', periodo: 'Diário', cor: 'bg-rose-100', iconCor: 'text-rose-600' },
    { id: '14', nome: 'Resumo Tributário', descricao: 'Impostos e retenções do período', periodo: 'Mensal', cor: 'bg-blue-100', iconCor: 'text-blue-600' }
  ];

  // Handlers
  const handleGenerateRelatorio = (relatorio) => {
    toast.success(`Gerando relatório: ${relatorio.nome}`);
  };

  const handleViewRelatorio = (relatorio) => {
    toast.info(`Visualizando: ${relatorio.nome}`);
  };

  const handleInsightAction = (insight) => {
    toast.success(`Executando ação: ${insight.acao}`);
  };

  const handleInsightFeedback = (insight, type) => {
    toast.success(type === 'up' ? 'Obrigado pelo feedback positivo!' : 'Feedback registrado. Vamos melhorar!');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Relatórios & IA</h1>
          <p className="text-slate-500">Inteligência de gestão e análises avançadas</p>
        </div>
        <div className="flex gap-2">
          <Select value={periodoSelecionado} onValueChange={setPeriodoSelecionado}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="dia">Hoje</SelectItem>
              <SelectItem value="semana">Esta semana</SelectItem>
              <SelectItem value="mes">Este mês</SelectItem>
              <SelectItem value="trimestre">Este trimestre</SelectItem>
              <SelectItem value="ano">Este ano</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={loadData} disabled={loading}>
            <RefreshCw className={cn("h-4 w-4 mr-2", loading && "animate-spin")} />
            Atualizar
          </Button>
        </div>
      </div>

      {/* KPIs Principais */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <MetricCard
          title="Alunos Ativos"
          value={metricas.alunosAtivos.toLocaleString()}
          icon={Users}
          color="violet"
          trend={5.2}
        />
        <MetricCard
          title="Receita Mensal"
          value={formatCurrency(metricas.receitaMensal)}
          icon={Wallet}
          color="emerald"
          trend={8.3}
        />
        <MetricCard
          title="Inadimplência"
          value={`${metricas.inadimplencia}%`}
          icon={AlertTriangle}
          color={parseFloat(metricas.inadimplencia) > 10 ? 'rose' : 'amber'}
          trend={parseFloat(metricas.inadimplencia) > 10 ? 2.1 : -1.5}
        />
        <MetricCard
          title="Média Geral"
          value={metricas.mediaGeral.toFixed(1)}
          icon={Award}
          color="blue"
          trend={0.3}
        />
        <MetricCard
          title="Frequência"
          value={`${metricas.frequenciaMedia}%`}
          icon={Calendar}
          color="violet"
          trend={1.2}
        />
        <MetricCard
          title="Satisfação"
          value={`${metricas.satisfacao}%`}
          icon={ThumbsUp}
          color="emerald"
          trend={3.5}
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-6 w-full max-w-3xl">
          <TabsTrigger value="executivo" className="flex items-center gap-1">
            <BarChart3 className="h-4 w-4" />
            <span className="hidden md:inline">Executivo</span>
          </TabsTrigger>
          <TabsTrigger value="academico" className="flex items-center gap-1">
            <GraduationCap className="h-4 w-4" />
            <span className="hidden md:inline">Acadêmico</span>
          </TabsTrigger>
          <TabsTrigger value="financeiro" className="flex items-center gap-1">
            <Wallet className="h-4 w-4" />
            <span className="hidden md:inline">Financeiro</span>
          </TabsTrigger>
          <TabsTrigger value="fiscal" className="flex items-center gap-1">
            <Receipt className="h-4 w-4" />
            <span className="hidden md:inline">Fiscal</span>
          </TabsTrigger>
          <TabsTrigger value="ia-operacional" className="flex items-center gap-1">
            <Zap className="h-4 w-4" />
            <span className="hidden md:inline">IA Operacional</span>
          </TabsTrigger>
          <TabsTrigger value="ia-gerencial" className="flex items-center gap-1">
            <Brain className="h-4 w-4" />
            <span className="hidden md:inline">IA Gerencial</span>
          </TabsTrigger>
        </TabsList>

        {/* Tab: Executivo */}
        <TabsContent value="executivo" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Gráfico de Receita */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-base">Evolução da Receita</CardTitle>
                <CardDescription>Receita mensal do período</CardDescription>
              </CardHeader>
              <CardContent>
                <SimpleBarChart data={tendencias.receita} height={200} />
              </CardContent>
            </Card>

            {/* Distribuição de Receita */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Composição da Receita</CardTitle>
                <CardDescription>Por tipo de serviço</CardDescription>
              </CardHeader>
              <CardContent>
                <SimplePieChart data={tendencias.distribuicao || []} />
              </CardContent>
            </Card>
          </div>

          {/* Relatórios Executivos */}
          <Card>
            <CardHeader>
              <CardTitle>Relatórios Executivos</CardTitle>
              <CardDescription>Visões consolidadas para tomada de decisão</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {relatoriosExecutivos.map((rel) => (
                <RelatorioCard
                  key={rel.id}
                  relatorio={rel}
                  onGenerate={handleGenerateRelatorio}
                  onView={handleViewRelatorio}
                />
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Acadêmico */}
        <TabsContent value="academico" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-emerald-100 rounded-xl">
                    <TrendingUp className="h-6 w-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Turmas acima da média</p>
                    <p className="text-2xl font-bold text-emerald-600">78%</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-amber-100 rounded-xl">
                    <BookOpen className="h-6 w-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Notas pendentes</p>
                    <p className="text-2xl font-bold text-amber-600">156</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-violet-100 rounded-xl">
                    <Award className="h-6 w-6 text-violet-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Taxa de aprovação</p>
                    <p className="text-2xl font-bold text-violet-600">94%</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Relatórios Acadêmicos</CardTitle>
              <CardDescription>Análises de desempenho e progresso</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {relatoriosAcademicos.map((rel) => (
                <RelatorioCard
                  key={rel.id}
                  relatorio={rel}
                  onGenerate={handleGenerateRelatorio}
                  onView={handleViewRelatorio}
                />
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Financeiro */}
        <TabsContent value="financeiro" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-emerald-100 rounded-xl">
                    <DollarSign className="h-6 w-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Receita realizada</p>
                    <p className="text-2xl font-bold text-emerald-600">{formatCurrency(metricas.receitaMensal)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-rose-100 rounded-xl">
                    <Percent className="h-6 w-6 text-rose-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Inadimplência</p>
                    <p className="text-2xl font-bold text-rose-600">{metricas.inadimplencia}%</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-100 rounded-xl">
                    <Target className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Meta do mês</p>
                    <p className="text-2xl font-bold text-blue-600">85%</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Relatórios Financeiros</CardTitle>
              <CardDescription>Análises de receita, cobrança e projeções</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {relatoriosFinanceiros.map((rel) => (
                <RelatorioCard
                  key={rel.id}
                  relatorio={rel}
                  onGenerate={handleGenerateRelatorio}
                  onView={handleViewRelatorio}
                />
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Fiscal */}
        <TabsContent value="fiscal" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-emerald-100 rounded-xl">
                    <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">NFS-e emitidas</p>
                    <p className="text-2xl font-bold text-emerald-600">456</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-amber-100 rounded-xl">
                    <Clock className="h-6 w-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Aguardando emissão</p>
                    <p className="text-2xl font-bold text-amber-600">23</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-rose-100 rounded-xl">
                    <AlertCircle className="h-6 w-6 text-rose-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Com erro</p>
                    <p className="text-2xl font-bold text-rose-600">5</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Relatórios Fiscais</CardTitle>
              <CardDescription>Análises de emissão e obrigações</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {relatoriosFiscais.map((rel) => (
                <RelatorioCard
                  key={rel.id}
                  relatorio={rel}
                  onGenerate={handleGenerateRelatorio}
                  onView={handleViewRelatorio}
                />
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: IA Operacional */}
        <TabsContent value="ia-operacional" className="space-y-6 mt-6">
          <Card className="bg-gradient-to-r from-violet-50 to-purple-50 border-violet-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white rounded-xl shadow-sm">
                  <Sparkles className="h-8 w-8 text-violet-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-violet-800">IA Operacional</h3>
                  <p className="text-violet-600">Insights automáticos para ações imediatas do dia a dia</p>
                </div>
                <div className="ml-auto">
                  <Badge className="bg-violet-600 text-white">
                    {insights.filter(i => i.prioridade === 'critica' || i.prioridade === 'alta').length} alertas ativos
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-4">
            {insights.map((insight) => (
              <InsightCard
                key={insight.id}
                insight={insight}
                onAction={handleInsightAction}
                onFeedback={handleInsightFeedback}
              />
            ))}

            {insights.length === 0 && (
              <Card className="py-12">
                <CardContent className="text-center">
                  <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto mb-4" />
                  <p className="text-slate-500">Nenhum alerta no momento. Tudo sob controle!</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Tab: IA Gerencial */}
        <TabsContent value="ia-gerencial" className="space-y-6 mt-6">
          <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white rounded-xl shadow-sm">
                  <Brain className="h-8 w-8 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-blue-800">IA Gerencial</h3>
                  <p className="text-blue-600">Análises estratégicas e projeções para decisões de longo prazo</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Previsão de Evasão */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <UserMinus className="h-5 w-5 text-rose-600" />
                  Previsão de Evasão
                </CardTitle>
                <CardDescription>Modelo preditivo baseado em histórico</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Risco Alto</span>
                    <div className="flex items-center gap-2">
                      <Progress value={15} className="w-32 h-2" />
                      <span className="font-medium text-rose-600">8 alunos</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Risco Médio</span>
                    <div className="flex items-center gap-2">
                      <Progress value={25} className="w-32 h-2" />
                      <span className="font-medium text-amber-600">15 alunos</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Risco Baixo</span>
                    <div className="flex items-center gap-2">
                      <Progress value={85} className="w-32 h-2" />
                      <span className="font-medium text-emerald-600">427 alunos</span>
                    </div>
                  </div>
                </div>
                <Button variant="outline" className="w-full mt-4">
                  Ver análise completa
                </Button>
              </CardContent>
            </Card>

            {/* Projeção de Receita */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-emerald-600" />
                  Projeção de Receita
                </CardTitle>
                <CardDescription>Estimativa para os próximos 3 meses</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="text-sm text-slate-600">Maio/2026</span>
                    <span className="font-bold text-slate-800">{formatCurrency(98000)}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="text-sm text-slate-600">Junho/2026</span>
                    <span className="font-bold text-slate-800">{formatCurrency(102000)}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
                    <span className="text-sm text-emerald-600">Julho/2026</span>
                    <span className="font-bold text-emerald-700">{formatCurrency(115000)}</span>
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-4">
                  * Projeção considera histórico de pagamentos e sazonalidade
                </p>
              </CardContent>
            </Card>

            {/* Comparativo de Unidades */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-violet-600" />
                  Benchmark de Unidades
                </CardTitle>
                <CardDescription>Comparativo de performance</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full" />
                      <span className="text-sm">Unidade Centro</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-slate-500">Média: 8.2</span>
                      <span className="text-sm text-slate-500">Inad: 5%</span>
                      <Badge className="bg-emerald-100 text-emerald-700">Top</Badge>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-amber-500 rounded-full" />
                      <span className="text-sm">Unidade Norte</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-slate-500">Média: 7.5</span>
                      <span className="text-sm text-slate-500">Inad: 12%</span>
                      <Badge className="bg-amber-100 text-amber-700">Atenção</Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Sugestões Estratégicas */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Lightbulb className="h-5 w-5 text-amber-600" />
                  Sugestões Estratégicas
                </CardTitle>
                <CardDescription>Recomendações baseadas em dados</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="p-3 bg-violet-50 rounded-lg">
                  <p className="text-sm text-violet-800">
                    <strong>Rematrícula:</strong> Iniciar campanha em maio pode aumentar retenção em 12% baseado no histórico.
                  </p>
                </div>
                <div className="p-3 bg-emerald-50 rounded-lg">
                  <p className="text-sm text-emerald-800">
                    <strong>Receita:</strong> Oferecer desconto de 5% para pagamento antecipado pode antecipar R$ 15.000 em fluxo de caixa.
                  </p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Acadêmico:</strong> Turmas do 7º ano precisam de reforço em Matemática baseado na tendência de notas.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
